import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'design-system-web';
import { Flex, Loading } from 'components';
import { Alert, AlertType } from 'components/lds';
import { H4 } from 'components/typography';
import { Channel } from 'lane-shared/types/ChannelType';
import { ViewExternalReferences } from 'components/externalReferences';
import { usePropertyExternalReferences } from '../hooks/usePropertyExternalReferences';
import styles from './PropertyExternalReferences.scss';
import { EditPropertyExternalReferences } from './EditPropertyExternalReferences';

type Props = {
  channel: Channel;
};

export const PropertyExternalReferences = ({ channel }: Props) => {
  const { t } = useTranslation();
  const [isEditMode, setIsEditMode] = useState(false);

  const { references, isLoadingReferences, getReferencesError, getReferences } =
    usePropertyExternalReferences(channel._id || '');

  useEffect(() => {
    if (channel._id) {
      getReferences();
    }
  }, [channel._id]);

  const handleRefetch = () => {
    getReferences();
  };

  const renderViewExternalReferences = () => {
    if (isLoadingReferences) {
      return (
        <Flex direction="row" gap={2} align="center">
          <Loading testId="loading-external-references-icon" />
          <span>
            {t(
              'web.admin.channel.profile.info.propertyExternalReferences.loading'
            )}
          </span>
        </Flex>
      );
    }

    if (getReferencesError) {
      return (
        <Flex direction="column" gap={2}>
          <Alert type={AlertType.error}>
            {t(
              'web.admin.channel.profile.info.propertyExternalReferences.error'
            )}
          </Alert>
          <Flex>
            <Button variant="secondary" size="medium" onClick={handleRefetch}>
              {t(
                'web.admin.channel.profile.info.propertyExternalReferences.button.retry'
              )}
            </Button>
          </Flex>
        </Flex>
      );
    }

    if (references.length === 0) {
      return (
        <Flex>
          <span>
            {t(
              'web.admin.channel.profile.info.propertyExternalReferences.noExternalReferences'
            )}
          </span>
        </Flex>
      );
    }

    return <ViewExternalReferences references={references} />;
  };

  return (
    <div className={styles.container}>
      <Flex direction="column" gap={3}>
        <Flex direction="row" gap={4}>
          <H4>{t`web.admin.channel.profile.info.propertyExternalReferences`}</H4>
          {!isEditMode && !isLoadingReferences && (
            <Button
              variant="secondary"
              testId="edit-property-external-references-button"
              size="medium"
              className="!px-2"
              onClick={() => setIsEditMode(true)}
            >
              {t(
                'web.admin.channel.profile.info.propertyExternalReferences.edit'
              )}
            </Button>
          )}
        </Flex>
        <Flex direction="column" className="mx-2">
          {!isEditMode && renderViewExternalReferences()}
          {isEditMode && (
            <EditPropertyExternalReferences
              references={references}
              channel={channel}
              onClose={() => setIsEditMode(false)}
            />
          )}
        </Flex>
      </Flex>
    </div>
  );
};
